import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { LunchMenu } from "../../../../api/lunchMenus";
import { useActiveCategories, useMenuAllergens } from "../../../../hooks/useMenu";
import { getRestaurantName } from "../../../../lib/restaurant";
import { useAppSelector } from "../../../../store/hooks";
import { fmtEuro } from "../../../../utils/currency";
import { getTranslation } from "../../../../utils/translations";
import { reportPrintCss } from "../../components/tables/reports/reportDocumentPrint";
import { DISPLAY_DATE, lunchPeriodText, lunchScheduleText } from "../lunchDraft";
import { lunchUnitOf, useLunchProducts } from "../useLunchProducts";
import { LunchAllergenLegend } from "./LunchAllergenLegend";

/**
 * Хартията на обедното меню за печат (A4 от браузъра): скрита на екрана,
 * `printReportDocument` я изважда в <body> и печата само нея — същият
 * механизъм като отчетите (report-doc-paper). Продуктите са по групите им в
 * основното меню; хартията е винаги светла (и в тъмна тема). До името —
 * номерата на алергените на продукта (артикул/рецепта), отдолу — легендата им.
 */
export const LunchMenuPrint = ({ menu }: { menu: LunchMenu }) => {
  const { t, i18n } = useTranslation();
  const productOf = useLunchProducts();
  const categories = useActiveCategories();
  const allergens = useMenuAllergens();
  const venueName = useAppSelector(
    (s) => s.auth.user?.restaurant_display_name ?? "",
  );

  const groups = useMemo(() => {
    const categoriesById = new Map(categories.map((category) => [category.id, category]));
    const grouped = new Map<number | null, typeof menu.items>();
    menu.items.forEach((lunchItem) => {
      const categoryId = productOf(lunchItem.itemId).item?.categoryId ?? null;
      grouped.set(categoryId, [...(grouped.get(categoryId) ?? []), lunchItem]);
    });
    return Array.from(grouped.entries())
      .map(([categoryId, items]) => {
        const category = categoryId != null ? categoriesById.get(categoryId) : undefined;
        return {
          key: categoryId ?? 0,
          title: category ? getTranslation(category.translations, i18n.language, category.slug) : "",
          sortIndex: category?.sortIndex ?? Number.MAX_SAFE_INTEGER,
          items,
        };
      })
      .sort((a, b) => a.sortIndex - b.sortIndex);
  }, [menu, categories, productOf, i18n.language]);

  const allergensById = useMemo(
    () => new Map(allergens.map((allergen) => [allergen.id, allergen])),
    [allergens],
  );
  // Номерът на алергена е id-то му (по закона: 1 глутен … 14 мекотели, 15 имитиращ продукт)
  const allergenNumbersOf = (itemId: number) =>
    (productOf(itemId).item?.allergenIds ?? [])
      .filter((allergenId) => allergensById.has(allergenId))
      .sort((a, b) => a - b);
  const legendAllergens = Array.from(
    new Set(menu.items.flatMap((lunchItem) => allergenNumbersOf(lunchItem.itemId))),
  )
    .sort((a, b) => a - b)
    .map((allergenId) => allergensById.get(allergenId)!);

  const singleDay = menu.dateFrom && menu.dateFrom === menu.dateTo && !menu.weekdays?.length;
  const dateLine = singleDay
    ? `${t(`lunchMenu.weekdayLong${dayjs(menu.dateFrom).day()}`)}, ${dayjs(menu.dateFrom).format(DISPLAY_DATE)} · ${menu.timeFrom}–${menu.timeTo}`
    : [lunchScheduleText(t, menu), lunchPeriodText(t, menu)].filter(Boolean).join(" · ");

  return (
    <>
      <style>{reportPrintCss}</style>
      <div className="report-doc-paper hidden bg-surface text-ink">
        <div className="flex flex-col items-center gap-2 border-b border-shade-2 pb-6 text-center">
          <span className="font-pos-mono text-[11px] font-semibold uppercase tracking-[.1em] text-muted">
            {venueName || getRestaurantName()}
          </span>
          {/* Името на менюто е вътрешно — на хартията винаги „Обедно меню" */}
          <span className="text-[34pt] font-extrabold leading-[1.2] tracking-[-0.5px]">
            {t("lunchMenu.title")}
          </span>
          <span className="text-[12pt] text-soft">{dateLine}</span>
        </div>
        <div className="mt-6 flex flex-col gap-5">
          {groups.map((group) => (
            <div key={group.key} className="flex flex-col">
              {group.title && (
                <span className="mb-1 font-pos-mono text-[10pt] font-semibold uppercase tracking-[.1em] text-brand-ink">
                  {group.title}
                </span>
              )}
              {group.items.map((lunchItem) => {
                const product = productOf(lunchItem.itemId);
                const unit = lunchUnitOf(lunchItem.unitAndSizeLangs, i18n.language, product.mainUnit);
                const allergenNumbers = allergenNumbersOf(lunchItem.itemId);
                return (
                  <div key={lunchItem.itemId} className="flex items-baseline gap-2.5 py-1.5">
                    <span className="text-[13pt] font-semibold">
                      {product.name}
                      {allergenNumbers.length > 0 && (
                        <sup className="ml-1 font-pos-mono text-[8pt] font-semibold text-soft">
                          {allergenNumbers.join(",")}
                        </sup>
                      )}
                    </span>
                    {unit.text && (
                      <span className="font-pos-mono text-[10pt] text-soft">{unit.text}</span>
                    )}
                    <span aria-hidden className="flex-1 -translate-y-1 border-b-[1.5px] border-dotted border-shade-4" />
                    <span className="font-pos-mono text-[13pt] font-bold">{fmtEuro(lunchItem.price)}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="mt-8 text-center text-[9pt] text-muted">
          {t("lunchMenu.printPricesNote")}
        </div>
        <LunchAllergenLegend allergens={legendAllergens} />
      </div>
    </>
  );
};
